'use client'
import { createContext, ReactNode, useEffect, useRef, useState } from 'react'
import { pickBy } from 'lodash'
import { onIdTokenChanged, User as FirebaseUser } from 'firebase/auth'
import { auth, firebaseLogout } from 'web/lib/firebase/users'
import { createUser } from 'web/lib/api/api'
import { useStateCheckEquality } from 'web/hooks/use-state-check-equality'
import {
  AUTH_COOKIE_NAME,
  BANNED_TRADING_USER_IDS,
  TEN_YEARS_SECS,
} from 'common/envs/constants'
import { getCookie, setCookie } from 'web/lib/util/cookie'
import {
  type PrivateUser,
  type User,
  type UserAndPrivateUser,
} from 'common/user'
import {
  nativePassUsers,
  nativeSetStreak,
  nativeSignOut,
} from 'web/lib/native/native-messages'
import { clearLocalStoragePreservingNativeInfo } from 'web/lib/native/is-native'
import { streakBonusPerDay } from 'common/native-message'
import { safeLocalStorage } from 'web/lib/util/local'
import { getSavedContractVisitsLocally } from 'web/hooks/use-save-visits'
import { getSupabaseToken } from 'web/lib/api/api'

import { useWebsocketUser, useWebsocketPrivateUser } from 'web/hooks/use-user'
import { trackUserChanges } from 'client-common/hooks/use-websocket-user'
import { useEffectCheckEquality } from 'web/hooks/use-effect-check-equality'
import { getPrivateUserSafe, getUserSafe } from 'web/lib/supabase/users'
import toast from 'react-hot-toast'
import { ensureDeviceToken } from 'web/lib/util/device-token'
import { Row } from './layout/row'
import { TokenNumber } from './widgets/token-number'
import { updateSupabaseAuth } from 'web/lib/supabase/db'
import { setLocalOnlyUserId } from 'common/util/api'

const IS_LOCAL_ONLY =
  typeof process !== 'undefined' &&
  process.env.NEXT_PUBLIC_LOCAL_ONLY === 'true'
const LOCAL_TEST_USER =
  typeof process !== 'undefined'
    ? process.env.NEXT_PUBLIC_LOCAL_TEST_USER
    : undefined

// Either we haven't looked up the logged in user yet (undefined), or we know
// the user is not logged in (null), or we know the user is logged in.
export type AuthUser =
  | undefined
  | null
  | (UserAndPrivateUser & { authLoaded: boolean })
const CACHED_USER_KEY = 'CACHED_USER_KEY_V2'
const getAdminToken = () => {
  const key = 'TEST_CREATE_USER_KEY'
  const cookie = getCookie(key)
  if (cookie) return cookie.replace(/"/g, '')

  // For our convenience. If there's a token in local storage, set it as a cookie
  const localStorageToken = safeLocalStorage?.getItem(key)
  if (localStorageToken) {
    setCookie(key, localStorageToken.replace(/"/g, ''))
  }
  return localStorageToken?.replace(/"/g, '') ?? ''
}

const stripUserData = (user: object) => {
  // there's some risk that this cookie could be too big for some clients,
  // so strip it down to only the keys that the server auth actually needs
  // in order to auth to the firebase SDK
  const whitelist = ['uid', 'emailVerified', 'isAnonymous', 'stsTokenManager']
  const stripped = pickBy(user, (_v, k) => whitelist.includes(k))
  // mqp: temp fix to get cookie size under 4k in edge cases
  delete (stripped as any).stsTokenManager.accessToken
  return JSON.stringify(stripped)
}

const setUserCookie = (data: object | undefined) => {
  const stripped = data ? stripUserData(data) : ''
  setCookie(AUTH_COOKIE_NAME, stripped, [
    ['path', '/'],
    ['max-age', (data === undefined ? 0 : TEN_YEARS_SECS).toString()],
    ['samesite', 'lax'],
    ['secure'],
  ])
}

export const AuthContext = createContext<AuthUser>(undefined)

export function AuthProvider(props: {
  children: ReactNode
  serverUser?: AuthUser
}) {
  const { children, serverUser } = props

  const [user, setUser] = useStateCheckEquality<User | undefined | null>(
    serverUser ? serverUser.user : serverUser
  )
  const [privateUser, setPrivateUser] = useStateCheckEquality<
    PrivateUser | undefined
  >(serverUser ? serverUser.privateUser : undefined)
  const [authLoaded, setAuthLoaded] = useState(false)
  // Whether this page session has ever observed a signed-in Firebase user. Gates
  // the sign-out we forward to the native app — see the null branch below.
  const sawFbUser = useRef(false)
  // Track observed Firebase identity separately from the asynchronously loaded
  // profile, so credentials clear as soon as an account switch begins.
  const supabaseUserId = useRef<string>()
  const committedUserId = useRef(user?.id)
  committedUserId.current = user?.id

  const authUser = !user
    ? user
    : !privateUser
    ? privateUser
    : { user, privateUser, authLoaded }

  useEffect(() => {
    if (serverUser === undefined) {
      const cachedUser = safeLocalStorage?.getItem(CACHED_USER_KEY)
      const parsed = cachedUser ? JSON.parse(cachedUser) : undefined
      if (parsed) {
        setUser(parsed.user)
        setPrivateUser(parsed.privateUser)
        setAuthLoaded(false)
      } else setUser(undefined)
    }
  }, [serverUser])

  useEffect(() => {
    if (authUser) {
      if (
        BANNED_TRADING_USER_IDS.includes(authUser.user.id) ||
        authUser.user.userDeleted
      ) {
        const message = authUser.user.userDeleted
          ? 'You have deleted the account associated with this email. To restore your account please email info@manifold.markets'
          : 'You are banned from trading. To learn more please email info@manifold.markets'

        firebaseLogout().then(() => {
          alert(message)
        })
        return
      }
      // Persist to local storage, to reduce login blink next time.
      // Note: Cap on localStorage size is ~5mb
      safeLocalStorage?.setItem(CACHED_USER_KEY, JSON.stringify(authUser))
    } else if (authUser === null) {
      safeLocalStorage?.removeItem(CACHED_USER_KEY)
    }
  }, [authUser])

  const onAuthLoad = (
    fbUser: FirebaseUser,
    user: User,
    privateUser: PrivateUser
  ) => {
    setUser(user)
    setPrivateUser(privateUser)
    setAuthLoaded(true)

    nativePassUsers(
      JSON.stringify({
        fbUser: fbUser.toJSON(),
        privateUser: privateUser,
      })
    )

    // generate auth token
    fbUser.getIdToken()
  }

  // LOCAL_ONLY mode: fetch user from local API instead of Firebase
  useEffect(() => {
    if (!IS_LOCAL_ONLY || !LOCAL_TEST_USER) return

    // Set the local user ID immediately so API calls include X-Local-User header
    const fetchLocalUser = async () => {
      try {
        const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'localhost:8088'
        const resp = await fetch(`http://${apiUrl}/v0/user/${LOCAL_TEST_USER}`)
        if (!resp.ok) {
          console.error('Failed to fetch local test user:', resp.statusText)
          setUser(null)
          setAuthLoaded(true)
          return
        }
        const userData = (await resp.json()) as User
        setLocalOnlyUserId(userData.id)
        setUser(userData)
        setPrivateUser({
          id: userData.id,
          notificationPreferences: {},
        } as PrivateUser)
        setAuthLoaded(true)
      } catch (e) {
        console.error('Error fetching local test user:', e)
        setUser(null)
        setAuthLoaded(true)
      }
    }
    fetchLocalUser()
  }, [])

  useEffect(() => {
    if (IS_LOCAL_ONLY) return // Skip Firebase auth in LOCAL_ONLY mode

    return onIdTokenChanged(
      auth,
      async (fbUser) => {
        if (auth.currentUser?.uid !== fbUser?.uid) return
        if (supabaseUserId.current !== fbUser?.uid) {
          updateSupabaseAuth(undefined)
          supabaseUserId.current = fbUser?.uid
        }
        // Firebase switches its request credential before the profile lookup
        // below finishes. Mark real identity changes transitional immediately
        // so requests cannot use the previous app user with the new token.
        // Ordinary same-user token refreshes should not blank the UI.
        if (fbUser?.uid !== committedUserId.current) {
          setAuthLoaded(false)
          // A known anonymous context does not carry authLoaded, so changing
          // null to undefined is what makes consumers wait during login.
          if (fbUser && committedUserId.current === undefined) {
            setUser(undefined)
          }
        }
        if (fbUser) {
          sawFbUser.current = true
          setUserCookie(fbUser.toJSON())

          // Tracked until the profile is set, so a change landing just as it
          // loads isn't missed.
          const changes = trackUserChanges(fbUser.uid)
          try {
            const [user, privateUser, supabaseJwt] = await Promise.all([
              getUserSafe(fbUser.uid),
              getPrivateUserSafe(),
              getSupabaseToken().catch((e) => {
                console.error('Error getting supabase token', e)
                return null
              }),
            ])
            // The fetches above can outlive the user they were for: a sign-out
            // or an account switch may have landed while they were in flight.
            // If so, this callback is stale — drop it BEFORE any global side
            // effect. updateSupabaseAuth in particular installs a token into
            // the shared Supabase REST/realtime client, so a stale callback
            // would otherwise leave Supabase authenticated as the old user
            // while Firebase/UI are the new one. Committing it would also push
            // the old user back to the native app (via onAuthLoad -> 'users').
            if (auth.currentUser?.uid !== fbUser.uid) return

            // A failed same-user refresh must not discard a still-valid JWT.
            // Logout/account changes already clear credentials above/below.
            if (supabaseJwt) updateSupabaseAuth(supabaseJwt.jwt)

            if (!user || !privateUser) {
              const deviceToken = ensureDeviceToken()
              const adminToken = getAdminToken()

              const newUser = (await createUser({
                deviceToken,
                adminToken,
                visitedContractIds: getSavedContractVisitsLocally(),
              })) as UserAndPrivateUser

              if (auth.currentUser?.uid !== fbUser.uid) return
              onAuthLoad(fbUser, newUser.user, newUser.privateUser)
            } else {
              // The profile may have been read before changes made while these
              // loaded, e.g. a setting saved during a token refresh.
              onAuthLoad(fbUser, { ...user, ...changes.since }, privateUser)
            }
          } finally {
            changes.stop()
          }
        } else {
          updateSupabaseAuth(undefined)
          // User logged out; reset to null
          setUserCookie(undefined)
          setUser(null)
          setPrivateUser(undefined)
          // Only propagate a sign-out we actually watched happen. Firebase fires
          // this observer with null once on init whenever nothing is persisted,
          // which inside the native WebView usually just means "the native
          // session hasn't been handed over yet" — and telling native to sign
          // out there makes it erase its stored credential and bounce the user
          // to the login screen. Deliberate logouts are unaffected:
          // firebaseLogout() calls nativeSignOut() itself.
          if (sawFbUser.current) {
            sawFbUser.current = false
            nativeSignOut()
          }
          // Clear local storage only if we were signed in, otherwise we'll clear referral info
          if (safeLocalStorage?.getItem(CACHED_USER_KEY))
            clearLocalStoragePreservingNativeInfo()
        }
      },
      (e) => {
        console.error(e)
      }
    )
  }, [])

  const uid = authUser ? authUser.user.id : authUser

  const listenUser = useWebsocketUser(uid ?? undefined, true)
  useEffectCheckEquality(() => {
    if (authLoaded && listenUser) {
      if (user) {
        const balanceChange = listenUser.balance - user.balance
        const cashBalanceChange = listenUser.cashBalance - user.cashBalance

        if (balanceChange > 0 || cashBalanceChange > 0) {
          showToast(balanceChange, cashBalanceChange)
        }
      }
      setUser(listenUser)
    }
  }, [authLoaded, listenUser])

  const listenPrivateUser = useWebsocketPrivateUser(uid ?? undefined)
  useEffectCheckEquality(() => {
    if (authLoaded && listenPrivateUser) setPrivateUser(listenPrivateUser)
  }, [authLoaded, listenPrivateUser])

  // Mirror the streak snapshot to the native app for the home/lock-screen
  // streak widget. Fires on initial load and whenever the live user's streak
  // fields change (e.g. right after a bet lights up the streak). No-ops on web.
  useEffect(() => {
    if (!user) return
    nativeSetStreak({
      loggedIn: true,
      streak: user.currentBettingStreak ?? 0,
      lastBetTime: user.lastBetTime ?? 0,
      lastStreakFreezeTime: user.lastStreakFreezeTime ?? 0,
      freezesLeft: user.streakForgiveness ?? 0,
      streakBonus: streakBonusPerDay(user),
      updatedAt: Date.now(),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.currentBettingStreak,
    user?.lastBetTime,
    user?.lastStreakFreezeTime,
    user?.streakForgiveness,
  ])

  return (
    <AuthContext.Provider value={authUser}>{children}</AuthContext.Provider>
  )
}

const showToast = (manaChange: number, cashChange: number) => {
  toast.success(
    <Row className="gap-1">
      <span>Cha-ching! Received</span>
      {manaChange > 0 && (
        <Row className="items-center justify-center">
          +
          <TokenNumber
            amount={manaChange}
            className="font-bold"
            coinType="MANA"
          />
          {cashChange > 0 && <span className="mx-1">&</span>}
        </Row>
      )}
      {cashChange > 0 && (
        <Row className="items-center justify-center">
          +
          <TokenNumber
            amount={cashChange}
            className="font-bold"
            coinType="CASH"
          />
        </Row>
      )}
    </Row>,
    { duration: 5000, icon: '🎉' }
  )
}
